import React from 'react';

import type { BoxProps, DialogContentProps, DialogProps, DialogTitleProps } from '@mui/material';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';

import { Button } from 'src/components/common';
import {
  DEFAULT_DIALOG_MAX_WIDTH,
  DEFAULT_MOBILE_PADDING,
  DEFAULT_PADDING,
  DIALOG_CONTENT_WITH_HEADER_PADDING_TOP,
} from 'src/components/common/Modal/constants';
import { useScreenInfo } from 'src/hooks/common';
import type { SxProp } from 'src/types/mui';

import type { ModalProps, ModalTopAction } from './types';

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  title,
  subtitle,
  dialogProps,
  headerProps,
  titleProps,
  headerPadding,
  contentProps,
  maxWidth = DEFAULT_DIALOG_MAX_WIDTH,
  padding: initialPadding = DEFAULT_PADDING,
  topAction,
  children,
}) => {
  const { isMobile } = useScreenInfo();
  const { hasTitle, hasHeader, hasSubtitle } = React.useMemo(() => {
    const hasTitle = !_.isNil(title);
    const hasSubtitle = !_.isNil(subtitle);
    const hasTopAction = !_.isNil(topAction);

    return {
      hasTitle,
      hasSubtitle,
      hasTopAction,
      hasHeader: hasTitle || hasTopAction,
    };
  }, [title, subtitle, topAction]);

  const padding = React.useMemo(() => {
    return initialPadding === false ? 0 : initialPadding;
  }, [initialPadding]);

  const dialogRootProps = React.useMemo(() => {
    let dialogWithWidthProps: Partial<DialogProps> = {};

    if (maxWidth !== false) {
      dialogWithWidthProps = {
        PaperProps: {
          sx: { maxWidth },
        },
      };
    }

    let mobileProps: Partial<DialogProps> = {};
    if (isMobile) {
      mobileProps = {
        PaperProps: {
          sx: { backgroundColor: 'slate.50' },
        },
      };
    }

    return _.merge({}, defaultDialogProps, dialogWithWidthProps, dialogProps, mobileProps);
  }, [dialogProps, isMobile, maxWidth]);

  const dialogHeaderProps = React.useMemo((): BoxProps => {
    const sxProp: SxProp = {
      padding: isMobile ? DEFAULT_MOBILE_PADDING : headerPadding || padding,
    };
    return _.merge({}, { sx: dialogHeaderStyles }, { sx: sxProp }, headerProps);
  }, [headerPadding, headerProps, isMobile, padding]);

  const dialogTitleProps = React.useMemo((): DialogTitleProps => {
    const sxProp: SxProp = {
      padding: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '90%',
    };
    return _.merge({}, { sx: sxProp }, titleProps);
  }, [titleProps]);

  const dialogContentProps = React.useMemo((): DialogContentProps => {
    const sxProp: SxProp = isMobile
      ? {
          padding: DEFAULT_MOBILE_PADDING,
          paddingTop: hasHeader ? DIALOG_CONTENT_WITH_HEADER_PADDING_TOP : DEFAULT_MOBILE_PADDING,
        }
      : {
          padding,
          paddingTop: hasHeader ? DIALOG_CONTENT_WITH_HEADER_PADDING_TOP : padding,
        };

    return _.merge({}, { sx: sxProp }, contentProps);
  }, [contentProps, hasHeader, isMobile, padding]);

  return (
    <Dialog {...dialogRootProps}>
      {hasHeader && (
        <Box {...dialogHeaderProps}>
          {hasTitle && <DialogTitle {...dialogTitleProps}>{title}</DialogTitle>}
          {hasSubtitle && <Typography sx={subtitleStyles}>Set up your payments</Typography>}
          {renderTopAction(topAction)}
        </Box>
      )}

      <DialogContent {...dialogContentProps}>{children}</DialogContent>
    </Dialog>
  );
};

const renderTopAction = (action?: ModalTopAction): React.ReactNode => {
  if (!action?.show) return null;

  if (action.render && _.isFunction(action.render)) {
    return action.render();
  }

  return (
    <Button
      variant="text"
      startIcon={action.icon}
      onClick={action.onAction}
      disabled={action.disabled}
      loading={action.loading}
    >
      {action.actionName}
    </Button>
  );
};

const dialogHeaderStyles: SxProp = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const defaultDialogProps: DialogProps = {
  open: false,
  maxWidth: false,
  fullWidth: true,
};

const subtitleStyles: SxProp = {
  fontWeight: 600,
  fontSize: '16px',
  color: 'rgba(0, 0, 0, 0.6)',
};
